import { useEffect, useRef } from "react";

const MEDALS = ["🥇", "🥈", "🥉", "4", "5", "6", "7", "8"];
const COMBO_COLORS = ["#ffffff", "#ffe600", "#ff8c00", "#ff3355"];
const TRACK_H = 48;
const TRACK_GAP = 10;
const PADDING_X = 12;
const PADDING_Y = 10;
const NAME_W = 76;
const CANVAS_W = 900;

function getCanvasHeight(count) {
  return PADDING_Y * 2 + count * TRACK_H + Math.max(0, count - 1) * TRACK_GAP;
}

function renderFrame(canvas, data, positions, finished, comboLevels, multipliers) {
  if (!canvas || !data) return;
  const ctx = canvas.getContext("2d");
  const W = canvas.width;
  ctx.clearRect(0, 0, W, canvas.height);

  const FINISH_POS = data.finish_pos || 1000;
  const racers = [...(data.players || [])].sort(
    (a, b) => (positions[b.name] || 0) - (positions[a.name] || 0)
  );
  const trackX = PADDING_X + NAME_W + 8;
  const trackW = W - trackX - PADDING_X;

  racers.forEach((racer, i) => {
    const pos = positions[racer.name] || 0;
    const pct = Math.min(pos / FINISH_POS, 1);
    const isFinished = finished.includes(racer.name);
    const finishIdx = finished.indexOf(racer.name);
    const combo = comboLevels[racer.name] || 0;
    const mult = multipliers[racer.name] || 1;

    const ty = PADDING_Y + i * (TRACK_H + TRACK_GAP);
    const cy = ty + TRACK_H / 2;
    const radius = TRACK_H * 0.38;

    // ── Player name ──────────────────────────────────────────────────────────
    ctx.save();
    ctx.font = "bold 12px 'Nunito', sans-serif";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.fillStyle = racer.color;
    ctx.shadowColor = racer.color;
    ctx.shadowBlur = 5;
    const dname = racer.name.length > 9 ? racer.name.slice(0, 8) + "…" : racer.name;
    ctx.fillText(dname, PADDING_X + NAME_W, cy);
    ctx.shadowBlur = 0;
    ctx.restore();

    // ── Track background (clipped pill shape) ────────────────────────────────
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(trackX, ty, trackW, TRACK_H, radius);
    ctx.clip(); // ALL drawing below is clipped to track shape

    // Road fill
    ctx.fillStyle = data.track?.bg || "#1a1a1a";
    ctx.fillRect(trackX, ty, trackW, TRACK_H);

    // Progress glow
    if (pct > 0.005) {
      const fillW = trackW * pct;
      const grad = ctx.createLinearGradient(trackX, 0, trackX + fillW, 0);
      grad.addColorStop(0, racer.color + "18");
      grad.addColorStop(1, racer.color + "50");
      ctx.fillStyle = grad;
      ctx.fillRect(trackX, ty, fillW, TRACK_H);
    }

    // Center dashes
    ctx.save();
    ctx.setLineDash([12, 12]);
    ctx.strokeStyle = "#ffffff12";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(trackX + radius, cy);
    ctx.lineTo(trackX + trackW - radius, cy);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // Finish line (checkered) — clipped inside track, flush to right edge
    const sqH = TRACK_H / 4;
    const sqW = sqH;
    const cols = 3;
    const finX = trackX + trackW - cols * sqW;
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < cols; col++) {
        ctx.fillStyle = (row + col) % 2 === 0 ? "#ffffff" : "#222222";
        ctx.globalAlpha = 0.55;
        ctx.fillRect(finX + col * sqW, ty + row * sqH, sqW, sqH);
      }
    }
    ctx.globalAlpha = 1;

    ctx.restore(); // end clip

    // ── Track border (drawn OVER clip so it's crisp) ─────────────────────────
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(trackX, ty, trackW, TRACK_H, radius);
    ctx.strokeStyle = racer.color + "50";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();

    // ── Car ──────────────────────────────────────────────────────────────────
    // carX travels from left-radius to right - 2*radius so car stays inside pill
    const travel = trackW - TRACK_H * 1.1;
    const carX = trackX + TRACK_H * 0.55 + travel * Math.min(pct, 0.97);

    if (isFinished) {
      ctx.font = `${TRACK_H * 0.6}px serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(MEDALS[finishIdx] || "✅", carX, cy);
    } else {
      // Combo glow ring
      if (combo > 0) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(carX, cy, TRACK_H * 0.36, 0, Math.PI * 2);
        ctx.strokeStyle = COMBO_COLORS[combo];
        ctx.lineWidth = 2.5;
        ctx.shadowColor = COMBO_COLORS[combo];
        ctx.shadowBlur = 16;
        ctx.stroke();
        ctx.restore();
      }

      // Car body (top-down, fits inside track height)
      const cw = TRACK_H * 0.36, ch = TRACK_H * 0.60;
      ctx.save();
      ctx.translate(carX, cy);
      ctx.shadowColor = racer.color;
      ctx.shadowBlur = combo > 0 ? 16 : 8;

      ctx.beginPath();
      ctx.roundRect(-cw / 2, -ch / 2, cw, ch, cw * 0.35);
      ctx.fillStyle = racer.color;
      ctx.fill();
      ctx.shadowBlur = 0;

      // Windshield
      ctx.beginPath();
      ctx.roundRect(-cw * 0.28, -ch * 0.27, cw * 0.56, ch * 0.27, 2);
      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.fill();

      // Wheels (4 corners, inside car bounds)
      [[-cw * 0.5, -ch * 0.27], [cw * 0.5, -ch * 0.27],
       [-cw * 0.5,  ch * 0.20], [cw * 0.5,  ch * 0.20]].forEach(([dx, dy]) => {
        ctx.beginPath();
        ctx.roundRect(dx - 2.5, dy - 4, 5, 8, 1.5);
        ctx.fillStyle = "#111";
        ctx.fill();
      });

      // Headlights
      ctx.beginPath();
      ctx.arc(-cw * 0.2, -ch * 0.44, 1.8, 0, Math.PI * 2);
      ctx.arc( cw * 0.2, -ch * 0.44, 1.8, 0, Math.PI * 2);
      ctx.fillStyle = "#fffbe0";
      ctx.fill();

      ctx.restore();

      // Multiplier label above car (outside clip, always visible)
      if (mult > 1.01) {
        ctx.save();
        ctx.font = "bold 10px 'Nunito', sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillStyle = COMBO_COLORS[combo] || "#fff";
        ctx.shadowColor = COMBO_COLORS[combo] || "#fff";
        ctx.shadowBlur = 5;
        ctx.fillText("x" + mult.toFixed(1), carX, ty + 1);
        ctx.restore();
      }
    }
  });
}

export default function TapraceHost({ data, positions, finished, comboLevels, multipliers, roundResult, lastEvent, players, scores, tv }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const stateRef = useRef({});
  stateRef.current = { data, positions, finished, comboLevels, multipliers };

  useEffect(() => {
    function loop() {
      const { data: d, positions: pos, finished: fin, comboLevels: cl, multipliers: mult } = stateRef.current;
      renderFrame(canvasRef.current, d, pos, fin, cl, mult);
      animRef.current = requestAnimationFrame(loop);
    }
    animRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  const count = (data?.players || players || []).length || 1;

  if (roundResult && !data) {
    const { order, points_awarded, scores: sc, track_name, round, max_rounds } = roundResult;
    return (
      <div className="w-full text-center animate-slide-up">
        <div style={{ marginBottom: "0.75rem" }}>
          <span className="font-heading text-[#ff8c00]" style={{ fontSize: tv ? "1.5rem" : "1rem" }}>{track_name}</span>
          <span className="font-heading text-[#555] ml-3" style={{ fontSize: tv ? "1rem" : "0.75rem" }}>Раунд {round}/{max_rounds}</span>
        </div>
        <div style={{ maxWidth: tv ? "680px" : "100%", margin: "0 auto", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {order.map((name, i) => {
            const pl = players.find(p => p.name === name);
            const pts = points_awarded[name] || 0;
            return (
              <div key={name} style={{
                background: "#1a1030", border: `2.5px solid ${i === 0 ? "#ffe600" : pl?.color || "#333"}`,
                borderRadius: "0.85rem", boxShadow: i === 0 ? "3px 3px 0 #ffe600" : "3px 3px 0 #000",
                padding: tv ? "0.85rem 1.25rem" : "0.5rem 0.85rem",
                display: "flex", alignItems: "center", gap: "0.75rem",
              }}>
                <span style={{ fontSize: tv ? "1.75rem" : "1.1rem", minWidth: "2rem" }}>{MEDALS[i] || `${i + 1}.`}</span>
                <span className="font-heading flex-1 text-left text-white" style={{ fontSize: tv ? "1.5rem" : "0.95rem" }}>{name}</span>
                <span className="font-display" style={{ color: "#ffe600", fontSize: tv ? "1.5rem" : "1rem" }}>+{pts}</span>
                <span className="font-display" style={{ color: pl?.color || "#aaa", fontSize: tv ? "1.25rem" : "0.85rem" }}>{sc[name] || 0}</span>
              </div>
            );
          })}
        </div>
        <p className="font-heading text-[#555] mt-3" style={{ fontSize: tv ? "0.9rem" : "0.7rem" }}>Следующий раунд...</p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="w-full animate-slide-up" style={{ userSelect: "none" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
        <div>
          <span className="font-heading" style={{ color: data.track?.color || "#ffe600", fontSize: tv ? "1.3rem" : "0.95rem" }}>
            {data.track?.name}
          </span>
          <span className="font-heading text-[#555] ml-2" style={{ fontSize: tv ? "0.8rem" : "0.6rem" }}>
            {data.track?.desc}
          </span>
        </div>
        <span className="font-heading text-[#555]" style={{ fontSize: tv ? "0.85rem" : "0.65rem" }}>
          Раунд {data.round}/{data.max_rounds}
        </span>
      </div>

      {lastEvent && (
        <div className="text-center animate-bounce-in" style={{ marginBottom: "0.35rem" }}>
          {lastEvent.type === "nitro"
            ? <span className="font-display" style={{ color: "#ff8c00", fontSize: tv ? "1.1rem" : "0.8rem" }}>🔥 Нитро! → {lastEvent.player}</span>
            : <span className="font-display" style={{ color: "#00f5ff", fontSize: tv ? "1.1rem" : "0.8rem" }}>🛢️ Масло! → {lastEvent.player}</span>
          }
        </div>
      )}

      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={getCanvasHeight(count)}
        style={{ width: "100%", display: "block", borderRadius: "0.5rem" }}
      />

      {finished.length > 0 && (
        <div className="flex gap-2 justify-center mt-2 flex-wrap">
          {finished.map((name, i) => (
            <div key={name} className="player-chip" style={{
              backgroundColor: players.find(p => p.name === name)?.color || "#666",
              fontSize: tv ? "1rem" : "0.75rem",
            }}>
              {MEDALS[i] || `${i + 1}`} {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}